// ProjectStageAgingReport.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Utilities
import {
  buildProjectWorkflowReportIndex,
  buildStageAgingRows,
  formatAgeLabel,
  PROJECT_WORKFLOW_AGING_SORT,
} from 'collection/reports/project/projectWorkflowReportIndex';

// CSS
import './ProjectWorkflowReportShared.css';

// Icons
import Icon from 'svg/Icon';

// Components
import SelectInput from 'components/selectinput/SelectInput';

const STATUS_LABELS = Object.freeze({
  blocked: 'Blocked',
  inProgress: 'In Progress',
  ready: 'Awaiting Action',
  running: 'Running',
});

/**
 * ProjectStageAgingReport
 *
 * Overview widget: open workflow steps ranked by overdue / age.
 */
const ProjectStageAgingReport = ({
  nodes = [],
  isLoading = false,
  title = 'Stage Aging',
  description = 'Open playbook steps past allotment or aging without a deadline.',
}) => {
  const { theme } = useTheme();
  const { openNode } = useNodeOpenIntent();

  const [templateKey, setTemplateKey] = useState('');
  const [assigneeKey, setAssigneeKey] = useState('');
  const [sortKey, setSortKey] = useState(PROJECT_WORKFLOW_AGING_SORT.OVERDUE);
  const [sortDirection, setSortDirection] = useState('desc');

  const index = useMemo(
    () => buildProjectWorkflowReportIndex({ nodes }),
    [nodes],
  );

  const rows = useMemo(() => (
    buildStageAgingRows(index.openSteps, {
      assigneeKey,
      sortDirection,
      sortKey,
      templateKey,
    })
  ), [assigneeKey, index.openSteps, sortDirection, sortKey, templateKey]);

  const templateOptions = useMemo(() => ([
    { label: 'All playbooks', value: '' },
    ...index.templateOptions,
  ]), [index.templateOptions]);

  const assigneeOptions = useMemo(() => ([
    { label: 'All assignees', value: '' },
    ...index.assigneeOptions.map((entry) => ({
      label: entry.label,
      value: entry.key,
    })),
  ]), [index.assigneeOptions]);

  const overdueCount = rows.filter((row) => row.overdue).length;

  const handleSortColumnClick = (columnKey) => {
    if (!columnKey) return;
    if (columnKey === sortKey) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(columnKey);
    setSortDirection(
      columnKey === PROJECT_WORKFLOW_AGING_SORT.PROJECT
        || columnKey === PROJECT_WORKFLOW_AGING_SORT.STEP
        || columnKey === PROJECT_WORKFLOW_AGING_SORT.ASSIGNEE
        ? 'asc'
        : 'desc',
    );
  };

  const renderSortableHeader = (columnKey, label) => {
    const isActive = columnKey === sortKey;
    return (
      <button
        aria-sort={
          isActive
            ? (sortDirection === 'asc' ? 'ascending' : 'descending')
            : 'none'
        }
        className="project-workflow-report-cell is-sortable"
        type="button"
        onClick={() => handleSortColumnClick(columnKey)}
      >
        <span>{label}</span>
        {isActive ? (
          <Icon
            color="currentColor"
            height="14px"
            iconKey={
              sortDirection === 'asc'
                ? 'keyboard_arrow_up'
                : 'keyboard_arrow_down'
            }
            width="14px"
          />
        ) : null}
      </button>
    );
  };

  return (
    <div className="project-workflow-report">
      <div className="project-workflow-report-intro">
        <div
          className="project-workflow-report-title"
          style={{ color: theme.foreground }}
        >
          {title}
        </div>
        <div
          className="project-workflow-report-description"
          style={{ color: theme.foregroundMuted }}
        >
          {isLoading
            ? description
            : rows.length
              ? `${rows.length} open ${rows.length === 1 ? 'step' : 'steps'}${overdueCount ? ` · ${overdueCount} overdue` : ''}`
              : description}
        </div>
      </div>

      <div className="project-workflow-report-toolbar">
        <div className="project-workflow-report-toolbar-group">
          <div
            className="project-workflow-report-filter-label"
            style={{ color: theme.foregroundMuted }}
          >
            Playbook
          </div>
          <div className="project-workflow-report-filter-select">
            <SelectInput
              height="44px"
              options={templateOptions}
              showIcons={false}
              value={templateKey}
              width="100%"
              onChange={(event) => setTemplateKey(event?.target?.value || '')}
            />
          </div>
        </div>

        <div className="project-workflow-report-toolbar-group">
          <div
            className="project-workflow-report-filter-label"
            style={{ color: theme.foregroundMuted }}
          >
            Assignee
          </div>
          <div className="project-workflow-report-filter-select">
            <SelectInput
              height="44px"
              options={assigneeOptions}
              showIcons={false}
              value={assigneeKey}
              width="100%"
              onChange={(event) => setAssigneeKey(event?.target?.value || '')}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div
          className="project-workflow-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="project-workflow-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          No open playbook steps.
        </div>
      ) : (
        <div className="project-workflow-report-table">
          <div className="project-workflow-report-row is-header">
            {renderSortableHeader(PROJECT_WORKFLOW_AGING_SORT.PROJECT, 'Project')}
            {renderSortableHeader(PROJECT_WORKFLOW_AGING_SORT.STEP, 'Step')}
            {renderSortableHeader(PROJECT_WORKFLOW_AGING_SORT.WORKFLOW, 'Playbook')}
            {renderSortableHeader(PROJECT_WORKFLOW_AGING_SORT.ASSIGNEE, 'Assignee')}
            {renderSortableHeader(PROJECT_WORKFLOW_AGING_SORT.AGE, 'Age')}
            {renderSortableHeader(PROJECT_WORKFLOW_AGING_SORT.OVERDUE, 'Overdue')}
          </div>

          {rows.map((row) => (
            <button
              key={`${row.projectKey}:${row.templateKey}:${row.stepKey}`}
              className="project-workflow-report-row is-clickable"
              type="button"
              onClick={() => openNode({ key: row.projectKey, title: row.projectTitle, type: 'PROJECT' })}
            >
              <div className="project-workflow-report-cell is-strong">
                {row.projectTitle}
              </div>
              <div className="project-workflow-report-cell">
                {row.stepTitle}
              </div>
              <div className="project-workflow-report-cell is-muted">
                {row.workflowTitle}
              </div>
              <div className="project-workflow-report-cell is-muted">
                {row.assigneeLabel}
              </div>
              <div className="project-workflow-report-cell is-muted">
                {formatAgeLabel(row.ageMs)}
              </div>
              <div
                className={`project-workflow-report-cell${row.overdue ? ' is-overdue' : ' is-muted'}`}
              >
                {row.overdue
                  ? formatAgeLabel(row.overdueMs)
                  : (STATUS_LABELS[row.status] || row.status)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

ProjectStageAgingReport.displayName = 'ProjectStageAgingReport';

export default ProjectStageAgingReport;
